import type { ReactNode } from 'react';
import type { EntryStatus, PaymentStatus } from '../lib/types';
import { labelFor } from '../lib/format';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <>
      <span className="spinner" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </>
  );
}

export function LoadingBlock({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

type AlertVariant = 'error' | 'success' | 'warning' | 'info';

export function Alert({
  variant = 'info',
  title,
  children,
}: {
  variant?: AlertVariant;
  title?: string;
  children?: ReactNode;
}) {
  // Errors interrupt; everything else is announced politely.
  const isUrgent = variant === 'error';
  return (
    <div
      className={`alert alert-${variant}`}
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
    >
      <div>
        {title && <div className="alert-title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {children}
      {action}
    </div>
  );
}

const STATUS_TONE: Record<EntryStatus, string> = {
  pending: 'badge-warn',
  confirmed: 'badge-ok',
  waitlisted: 'badge-info',
  withdrawn: 'badge-neutral',
  dns: 'badge-neutral',
  dnf: 'badge-neutral',
  dsq: 'badge-danger',
};

const PAYMENT_TONE: Record<PaymentStatus, string> = {
  unpaid: 'badge-warn',
  paid: 'badge-ok',
  refunded: 'badge-neutral',
  waived: 'badge-info',
};

export function StatusBadge({ status }: { status: EntryStatus }) {
  return <span className={`badge ${STATUS_TONE[status] ?? 'badge-neutral'}`}>{labelFor(status)}</span>;
}

export function PaymentBadge({ status }: { status: PaymentStatus }) {
  return (
    <span className={`badge ${PAYMENT_TONE[status] ?? 'badge-neutral'}`}>{labelFor(status)}</span>
  );
}

export function StatCard({
  label,
  value,
  sub,
  meter,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** 0-1 fraction; renders a capacity bar. */
  meter?: number;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
      {meter !== undefined && (
        <div
          className="meter"
          role="progressbar"
          aria-valuenow={Math.round(meter * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} progress`}
        >
          <div className="meter-fill" style={{ width: `${Math.min(100, meter * 100)}%` }} />
        </div>
      )}
    </div>
  );
}

/** Horizontal bar list for the status/category/age breakdowns. */
export function Breakdown({
  title,
  data,
  formatLabel = labelFor,
}: {
  title: string;
  data: Record<string, number>;
  formatLabel?: (key: string) => string;
}) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = entries.reduce((m, [, count]) => Math.max(m, count), 0);

  return (
    <div className="card card-tight">
      <h3>{title}</h3>
      {entries.length === 0 ? (
        <p className="faint">No data yet.</p>
      ) : (
        <ul className="breakdown">
          {entries.map(([key, count]) => (
            <li key={key}>
              <span>{formatLabel(key)}</span>
              <span className="mono">{count}</span>
              <span className="breakdown-bar">
                <span style={{ width: max > 0 ? `${(count / max) * 100}%` : '0%' }} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
